import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@medikei/ui';
import { Button } from '@medikei/ui';
import { Zap, Clock, Users, ArrowRight, RefreshCw, Banknote, Calendar } from 'lucide-react';
import { expressApi } from '@/api';
import { Skeleton } from '@medikei/ui';

interface ExpressCardProps {
  className?: string;
}

export function ExpressCard({ className }: ExpressCardProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [doctorsOnline, setDoctorsOnline] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const [formattedPrice, setFormattedPrice] = useState('R$ 89,00');
  const [hasError, setHasError] = useState(false);

  const loadData = useCallback(async (isInitial = true) => {
    const isInitialLoad = typeof isInitial === 'boolean' ? isInitial : true;
    if (isInitialLoad) {
      setIsLoading(true);
      setHasError(false);
    }
    try {
      const [doctorsData, pricingData, queueStatus] = await Promise.all([
        expressApi.getDoctorsOnline(),
        typeof expressApi?.getPricing === 'function'
          ? expressApi.getPricing().catch(() => null)
          : null,
        typeof expressApi?.getQueueStatus === 'function'
          ? expressApi.getQueueStatus().catch(() => null)
          : null,
      ]);

      // httpClient pode desempacotar o array de médicos diretamente caso a resposta contenha 'data'
      let count = 0;
      if (Array.isArray(doctorsData)) {
        count = doctorsData.length;
      } else if (doctorsData && typeof doctorsData === 'object') {
        count = (doctorsData as any).count ?? (doctorsData as any).data?.length ?? 0;
      }

      // Fallback de alta disponibilidade com a métrica da fila virtual
      if (count === 0 && queueStatus?.availableDoctors && queueStatus.availableDoctors > 0) {
        count = queueStatus.availableDoctors;
      }

      setDoctorsOnline(count);
      if (pricingData?.formattedPrice) {
        setFormattedPrice(pricingData.formattedPrice);
      } else if (typeof (pricingData as any)?.price === 'number') {
        const safePrice = (pricingData as any).price >= 1000 ? (pricingData as any).price / 100 : (pricingData as any).price;
        setFormattedPrice(
          new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(safePrice)
        );
      }

      if (count > 0) {
        const dynamicTime = Math.max(5, Math.min(15, Math.round(15 / count)));
        setEstimatedTime(dynamicTime);
      } else {
        setEstimatedTime(null);
      }
      setHasError(false);
    } catch (error) {
      console.error('Failed to load express data:', error);
      if (isInitialLoad) {
        setHasError(true);
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData(true);

    // Polling contínuo e rápido a cada 3 segundos para refletir médicos online em tempo real
    const interval = setInterval(() => {
      loadData(false);
    }, 3000);

    // Atualização imediata ao focar na janela ou voltar para a aba
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData(false);
      }
    };
    const handleFocus = () => {
      loadData(false);
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  const handleStartExpress = () => {
    setIsStarting(true);
    navigate('/expressa?step=payment');
  };

  const handleScheduleRegular = () => {
    navigate('/agendamentos');
  };

  return (
    <Card className={`p-5 sm:p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 text-white shadow-xl border-0 relative overflow-hidden ${className}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Zap className="w-5 h-5 text-amber-300" aria-hidden="true" />
            <h3 className="font-bold text-lg sm:text-xl tracking-tight">Consulta Expressa (Plantão 24h)</h3>
          </div>
          <p className="text-blue-100 text-xs sm:text-sm mb-4">
            Atendimento médico imediato online sem necessidade de agendamento prévio
          </p>
        </div>
      </div>

      {hasError ? (
        <div className="bg-white/10 rounded-xl p-4 mb-4 text-center border border-white/15">
          <p className="text-blue-50 text-sm mb-3">
            Não foi possível carregar os dados da Telemedicina Expressa.
          </p>
          <Button
            onClick={() => loadData(true)}
            className="bg-white text-blue-600 hover:bg-blue-50 gap-2 font-semibold rounded-xl"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div className="bg-white/10 backdrop-blur-xs rounded-xl p-3 sm:p-3.5 border border-white/10">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-200 shrink-0" aria-hidden="true" />
                <span className="text-blue-200 text-[11px] sm:text-xs font-medium truncate">Tempo estimado</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-6 w-12 bg-white/20" />
              ) : (
                <p className="text-xl sm:text-2xl font-black tracking-tight">
                  {estimatedTime ? `${estimatedTime} min` : '—'}
                </p>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-xs rounded-xl p-3 sm:p-3.5 border border-white/10">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-200 shrink-0" aria-hidden="true" />
                <span className="text-blue-200 text-[11px] sm:text-xs font-medium truncate">Médicos online</span>
              </div>
              {isLoading ? (
                <Skeleton className="h-6 w-8 bg-white/20" />
              ) : (
                <p className="text-xl sm:text-2xl font-black tracking-tight">
                  {doctorsOnline > 0 ? (
                    <span className="text-emerald-300">{doctorsOnline} online</span>
                  ) : (
                    <span className="text-xs font-semibold text-amber-200">Sem plantonista</span>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-white/20 my-3.5 pt-3.5 sm:my-4 sm:pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Banknote className="w-4 h-4 text-white/80" aria-hidden="true" />
                <span className="text-white/80 text-xs sm:text-sm font-medium">Valor da consulta</span>
              </div>
              <p className="text-lg sm:text-xl font-black tracking-tight">{formattedPrice}</p>
            </div>
          </div>
        </>
      )}

      {doctorsOnline > 0 ? (
        <Button
          onClick={handleStartExpress}
          className="w-full bg-white text-blue-700 hover:bg-blue-50 gap-2 font-bold text-sm sm:text-base h-11 sm:h-12 shadow-lg rounded-xl transition-all"
          disabled={isLoading || isStarting}
          loading={isStarting}
          aria-label="Iniciar consulta de Telemedicina Expressa"
        >
          Iniciar Agora
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-blue-100 text-center">
            Nenhum médico de plantão no momento. Agende para o próximo horário disponível.
          </p>
          <Button
            onClick={handleScheduleRegular}
            className="w-full bg-white/20 hover:bg-white/30 text-white gap-2 font-bold rounded-xl border border-white/30 h-11 sm:h-12"
          >
            <Calendar className="w-4 h-4" aria-hidden="true" />
            Agendar Consulta
          </Button>
        </div>
      )}
    </Card>
  );
}
